import resolveConfig from 'tailwindcss/resolveConfig'
import tailwindConfig from '../../../../../../tailwind.config.ts'

import { capitalizeFirstLetter } from '../../../../../app/utils/capitalizeFirstLetter'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { TransactionChartResponse } from '../../../../../app/services/transactionService/transactionChart'
import { formatMoneyValue } from '../../../../../app/utils/formatMoneyValue'
import { getPartnerBgColor } from '../../../../../app/utils/partners'
import { PartnerType, getPartnerLabel } from '../../../../../app/utils/partners.enum'
import { ChartOptions } from 'chart.js'

const theme = resolveConfig(tailwindConfig)
const primaryColor = theme.theme.colors.primary as string

const getTypeColor = (type: string): string => {
  const partnerType = type as PartnerType
  if (Object.values(PartnerType).includes(partnerType)) {
    return getPartnerBgColor(partnerType)
  }
  return primaryColor
}

const formatYAxisLabel = (value: number | string) => {
  const num = typeof value === 'string' ? parseFloat(value) : value

  if (isNaN(num)) return '0'

  const length = Math.abs(num).toString().length

  if (length > 9) return num / 1_000_000_000 + 'B'

  if (length > 6) return num / 1_000_000 + 'M'

  if (length > 3) return num / 1_000 + 'k'

  return `${num}`
}

const options: ChartOptions<'line'> = {
  responsive: true,
  interaction: {
    mode: 'index',
    intersect: false,
  },
  plugins: {
    legend: {
      display: true,
      position: 'top',
      labels: {
        usePointStyle: true,
        pointStyle: 'circle',
        padding: 15,
        boxHeight: 12,
        font: {
          size: 14,
          weight: 600,
        },
      },
    },
    tooltip: {
      callbacks: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        label: (context: any) => {
          const datasetIndex = context.datasetIndex
          const dataIndex = context.dataIndex
          const dataset = context.chart.data.datasets[datasetIndex]
          const transactionCount = dataset.totals?.[dataIndex]?.count || 0
          const transactionValue = formatMoneyValue(
            dataset.totals?.[dataIndex]?.total || 0,
          )
          const typeName = dataset.label

          return [
            `${typeName}`,
            `Transações realizadas: ${transactionCount}`,
            `Movimentado: ${transactionValue}`,
          ]
        },
      },
      displayColors: true,
    },
  },
  maintainAspectRatio: false,
  aspectRatio: 1.2,
  scales: {
    x: {
      grid: {
        display: false,
      },
      ticks: {
        display: false,
        font: {
          size: 16,
          weight: 600,
        },
      },
    },
    y: {
      grid: {
        offset: true,
      },
      border: {
        dash: [8, 4],
      },
      ticks: {
        callback: formatYAxisLabel,
        font: {
          size: 16,
          weight: 600,
        },
        precision: 1,
      },
    },
  },
  elements: {
    line: {
      tension: 0.4,
    },
    point: {
      radius: 4,
      hoverRadius: 6,
    },
  },
}

export function useTransactionChart(
  graphData: TransactionChartResponse | undefined,
  metric: 'total' | 'count' = 'count',
) {
  if (!graphData?.data || graphData.data.length === 0) {
    return {
      data: {
        labels: [],
        datasets: [],
      },
      options,
    }
  }

  const allDates = Array.from(
    new Set(graphData.data.map((item) => item.date)),
  ).sort((a, b) => new Date(a).getTime() - new Date(b).getTime())

  const uniqueTypes = new Set<PartnerType>()
  graphData.data.forEach((item) => {
    Object.keys(item).forEach((key) => {
      if (key.endsWith('_total')) {
        const type = key.replace('_total', '') as PartnerType
        if (Object.values(PartnerType).includes(type)) {
          uniqueTypes.add(type)
        }
      }
    })
  })

  const dataMap = new Map<
    PartnerType,
    Map<string, { total: number; count: number }>
  >()

  graphData.data.forEach((item) => {
    uniqueTypes.forEach((type) => {
      const totalKey = `${type}_total`
      const countKey = `${type}_count`

      const total = item[totalKey] as number | undefined
      const count = item[countKey] as number | undefined

      if (total !== undefined || count !== undefined) {
        if (!dataMap.has(type)) {
          dataMap.set(type, new Map())
        }
        const typeMap = dataMap.get(type)!
        typeMap.set(item.date, {
          total: total ?? 0,
          count: count ?? 0,
        })
      }
    })
  })

  const datasets = Array.from(uniqueTypes).map((type) => {
    const typeData = dataMap.get(type)!
    const data = allDates.map((date) => {
      const item = typeData.get(date)
      if (!item) return null
      if (metric === 'total') return item.total / 100
      return item[metric]
    })
    const totals = allDates.map((date) => {
      const item = typeData.get(date)
      return item || { total: 0, count: 0 }
    })

    const typeColor = getTypeColor(type)

    return {
      label: getPartnerLabel(type),
      data,
      borderColor: typeColor,
      backgroundColor: `${typeColor}30`,
      borderWidth: 3,
      fill: true,
      totals,
      tension: 0.4,
      pointBackgroundColor: typeColor,
      pointBorderColor: typeColor,
      pointBorderWidth: 0,
    }
  })

  const labels = allDates.map((date) =>
    capitalizeFirstLetter(format(new Date(date), 'dd/MM', { locale: ptBR })),
  )

  return {
    data: {
      labels,
      datasets,
    },
    options,
  }
}
